import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doEngageContactEvacuationInfoSave,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { View } from 'react-native';
import { format } from 'date-fns';
import { Txt } from 'components/Txt';
import { DateField } from 'components/Field/DateField';
import { API_DATE_FORMAT } from 'lib/constants';
import { TextAreaFieldWithCharacterCount } from 'components/Field/TextAreaFieldWithCharacterCount';
import { EngageContactEvacuationInfo } from 'types/DTOs/BlockManagerDTOs';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { dateToIsoString, isoDateStringToDate, arrayJoin } from 'lib/riskbase';
import { ISODateString } from 'types/types';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { isEqual } from 'lodash';

const validateData = (data: ModalEngageContactEvacuationInfo) => {
  const missingValues: string[] = [];
  const messages: string[] = [];

  if (data.hasAgreedEmergencyEvacuationStatement) {
    if (
      !data.emergencyEvacuationStatement ||
      data.emergencyEvacuationStatement.trim() === ''
    ) {
      missingValues.push('Emergency Evacuation Statement (EES)');
      messages.push('provide the Emergency Evacuation Statement (EES)');
    }
  }

  if (data.hasAgreedEmergencyEvacuationSharing) {
    if (
      !data.emergencyEvacuationAssistanceSummary ||
      data.emergencyEvacuationAssistanceSummary.trim() === ''
    ) {
      missingValues.push('Emergency Evacuation Assistance Summary (EEAS)');
      messages.push(
        'provide the Emergency Evacuation Assistance Summary (EEAS)',
      );
    }
  }

  return {
    hasErrors: missingValues.length > 0,
    title: `Missing ${arrayJoin(missingValues, ', ', ' and ')}`,
    text: `Please ${arrayJoin(messages, ', ', ' and ')} agreed with the resident`,
  };
};

type ModalEngageContactEvacuationInfo = EngageContactEvacuationInfo & {
  hasAgreedEmergencyEvacuationStatement: boolean;
  hasAgreedEmergencyEvacuationSharing: boolean;
};

function getSubmitData(
  data: ModalEngageContactEvacuationInfo,
): EngageContactEvacuationInfo {
  return !data.hasSupportNeeds
    ? {
        hasSupportNeeds: false,
        emergencyEvacuationStatementAgreedDate: null,
        emergencyEvacuationSharingAgreedDate: null,
        emergencyEvacuationAssistanceSummary: '',
        emergencyEvacuationStatement: '',
        hasCognitiveImpairment: false,
        hasHearingImpairment: false,
        hasMobilityLimitation: false,
        hasSocialAnxiety: false,
      }
    : {
        hasSupportNeeds: data.hasSupportNeeds,
        emergencyEvacuationStatementAgreedDate:
          data.hasAgreedEmergencyEvacuationStatement
            ? data.emergencyEvacuationStatementAgreedDate
            : null,
        emergencyEvacuationSharingAgreedDate:
          data.hasAgreedEmergencyEvacuationSharing
            ? data.emergencyEvacuationSharingAgreedDate
            : null,
        emergencyEvacuationAssistanceSummary:
          data.emergencyEvacuationAssistanceSummary,
        emergencyEvacuationStatement: data.emergencyEvacuationStatement,
        hasCognitiveImpairment: data.hasCognitiveImpairment,
        hasHearingImpairment: data.hasHearingImpairment,
        hasMobilityLimitation: data.hasMobilityLimitation,
        hasSocialAnxiety: data.hasSocialAnxiety,
      };
}

export const EngageContactEmergencyEvacuationModal: React.FC<
  ModalScreenProps<ModalScreens.EngageContactEmergencyEvacuation>
> = ({ route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const {
    contactName,
    contactId,
    flatId,
    propertyId,
    data: initialData,
    key,
  } = route.params;
  const confirmUnsaved = useConfirmUnsaved();

  const data = React.useRef<ModalEngageContactEvacuationInfo>({
    ...initialData,
    hasAgreedEmergencyEvacuationStatement:
      !!initialData.emergencyEvacuationStatementAgreedDate,
    emergencyEvacuationStatementAgreedDate:
      !!initialData.emergencyEvacuationStatementAgreedDate
        ? initialData.emergencyEvacuationStatementAgreedDate
        : dateToIsoString(new Date()),
    hasAgreedEmergencyEvacuationSharing:
      !!initialData.emergencyEvacuationSharingAgreedDate,
    emergencyEvacuationSharingAgreedDate:
      !!initialData.emergencyEvacuationSharingAgreedDate
        ? initialData.emergencyEvacuationSharingAgreedDate
        : dateToIsoString(new Date()),
  });

  const handleSave = React.useCallback(() => {
    const { hasErrors, title, text } = validateData(data.current);

    if (hasErrors) {
      dispatch(
        ALERT({
          title,
          text,
        }),
      );
      return;
    }

    const newData = getSubmitData(data.current);

    if (isEqual(newData, initialData)) {
      dispatch(NAVIGATE_MODAL_BACK());
      return;
    }

    dispatch(
      doEngageContactEvacuationInfoSave({
        key,
        propertyId,
        flatId,
        contactId,
        info: newData,
      }),
    );
  }, [dispatch, key, propertyId, flatId, contactId, initialData, data]);

  const handleClose = React.useCallback(() => {
    if (!isEqual(getSubmitData(data.current), initialData)) {
      confirmUnsaved({
        onSave: () => handleSave(),
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
    } else {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, confirmUnsaved, handleSave, data, initialData]);

  const updateCheckValue = React.useCallback(
    (
      field: keyof Pick<
        ModalEngageContactEvacuationInfo,
        | 'hasSupportNeeds'
        | 'hasSocialAnxiety'
        | 'hasMobilityLimitation'
        | 'hasHearingImpairment'
        | 'hasCognitiveImpairment'
        | 'hasAgreedEmergencyEvacuationStatement'
        | 'hasAgreedEmergencyEvacuationSharing'
      >,
      value: boolean,
    ) => {
      data.current[field] = value;
      forceUpdate();
    },
    [data, forceUpdate],
  );

  const updateTextValue = React.useCallback(
    (
      field: keyof Pick<
        typeof data.current,
        'emergencyEvacuationAssistanceSummary' | 'emergencyEvacuationStatement'
      >,
      value: string,
    ) => {
      data.current[field] = value;
      forceUpdate();
    },
    [data, forceUpdate],
  );

  const updateDateValue = React.useCallback(
    (
      field: keyof Pick<
        ModalEngageContactEvacuationInfo,
        | 'emergencyEvacuationStatementAgreedDate'
        | 'emergencyEvacuationSharingAgreedDate'
      >,
      value: Date | null,
    ) => {
      (data.current[field] as ISODateString | null) = value
        ? dateToIsoString(new Date(value))
        : null;
      forceUpdate();
    },
    [data, forceUpdate],
  );

  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Vulnerability, PEEP and PCFRA Management"
          subheading={contactName}
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card
        label="Help Required When Evacuating"
        labelSize="large"
        explanation="This section describes any help this resident requires when evacuating their flat in an emergency."
        marginBottomHalf
      ></Card>
      <Card marginBottomQuarter>
        <CardRow
          label="Resident requires help evacuating in an emergency"
          checkedValue={data.current.hasSupportNeeds}
          onPress={() =>
            updateCheckValue(
              'hasSupportNeeds',
              data.current.hasSupportNeeds ? false : true,
            )
          }
        />
      </Card>
      {data.current.hasSupportNeeds && (
        <>
          <Card>
            <CardRow
              label="Needs help with stairs"
              checkedValue={data.current.hasMobilityLimitation}
              onPress={() =>
                updateCheckValue(
                  'hasMobilityLimitation',
                  !data.current.hasMobilityLimitation,
                )
              }
            />
            <CardRow
              label="Struggles to follow instructions"
              checkedValue={data.current.hasCognitiveImpairment}
              onPress={() =>
                updateCheckValue(
                  'hasCognitiveImpairment',
                  !data.current.hasCognitiveImpairment,
                )
              }
            />
            <CardRow
              label="Finds crowded spaces stressful"
              checkedValue={data.current.hasSocialAnxiety}
              onPress={() =>
                updateCheckValue(
                  'hasSocialAnxiety',
                  !data.current.hasSocialAnxiety,
                )
              }
            />
            <CardRow
              label="Has a hearing impairment"
              checkedValue={data.current.hasHearingImpairment}
              onPress={() =>
                updateCheckValue(
                  'hasHearingImpairment',
                  !data.current.hasHearingImpairment,
                )
              }
            />
          </Card>

          <Card
            label="Emergency Evacuation Statement"
            labelSize="large"
            required={data.current.hasAgreedEmergencyEvacuationStatement}
            // explanation="An EES sets out what the resident should do in the event of a fire."
            style={{ marginBottom: -26 }}
          ></Card>
          <TextAreaFieldWithCharacterCount
            explanation="Describe how the resident would evacuate in an emergency, including any assistance, equipment, or agreed actions needed."
            placeholder="No Emergency Evacuation Statement provided"
            required={data.current.hasAgreedEmergencyEvacuationStatement}
            onChange={(value) => {
              updateTextValue('emergencyEvacuationStatement', value);
            }}
            maxLength={1000}
            value={data.current.emergencyEvacuationStatement || ''}
            marginBottomHalf
          />
          <Card>
            <CardRow
              label="Resident has an agreed Emergency Evacuation Statement"
              checkedValue={data.current.hasAgreedEmergencyEvacuationStatement}
              onPress={() => {
                updateCheckValue(
                  'hasAgreedEmergencyEvacuationStatement',
                  !data.current.hasAgreedEmergencyEvacuationStatement,
                );
              }}
            />
            {data.current.hasAgreedEmergencyEvacuationStatement && (
              <CardRow>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginVertical: -theme.paddingHorizontal,
                  }}
                >
                  <Txt>Date Agreed: </Txt>
                  <DateField
                    value={
                      data.current.emergencyEvacuationStatementAgreedDate
                        ? format(
                            isoDateStringToDate(
                              data.current
                                .emergencyEvacuationStatementAgreedDate,
                            ),
                            API_DATE_FORMAT,
                          )
                        : ''
                    }
                    onChange={(value) => {
                      updateDateValue(
                        'emergencyEvacuationStatementAgreedDate',
                        new Date(value),
                      );
                    }}
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    clearable={false}
                    showFieldTypeIcon={false}
                  />
                </View>
              </CardRow>
            )}
          </Card>

          <TextAreaFieldWithCharacterCount
            label="Evacuation Assistance Summary"
            explanation="Provide a brief summary of the level of assistance required for evacuation. This may be shared with the Fire and Rescue Service and must be short and clear."
            placeholder="No Evacuation Assistance Summary provided"
            required={data.current.hasAgreedEmergencyEvacuationSharing}
            onChange={(value) => {
              updateTextValue('emergencyEvacuationAssistanceSummary', value);
            }}
            maxLength={300}
            value={data.current.emergencyEvacuationAssistanceSummary}
            marginBottomHalf
          />
          <Card>
            <CardRow
              label="Resident has agreed to share relevant information with the FRS"
              checkedValue={data.current.hasAgreedEmergencyEvacuationSharing}
              onPress={() => {
                updateCheckValue(
                  'hasAgreedEmergencyEvacuationSharing',
                  !data.current.hasAgreedEmergencyEvacuationSharing,
                );
              }}
            />
            {data.current.hasAgreedEmergencyEvacuationSharing && (
              <CardRow>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginVertical: -theme.paddingHorizontal,
                  }}
                >
                  <Txt>Date Agreed:</Txt>
                  <DateField
                    value={
                      data.current.emergencyEvacuationSharingAgreedDate
                        ? format(
                            isoDateStringToDate(
                              data.current.emergencyEvacuationSharingAgreedDate,
                            ),
                            API_DATE_FORMAT,
                          )
                        : ''
                    }
                    onChange={(value) => {
                      updateDateValue(
                        'emergencyEvacuationSharingAgreedDate',
                        new Date(value),
                      );
                    }}
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    clearable={false}
                    showFieldTypeIcon={false}
                  />
                </View>
              </CardRow>
            )}
          </Card>
        </>
      )}
    </PageScrollView>
  );
};
